/**
 * PegasusJobDetail (Seth 2026-06-24 redesign) — a Pegasus-scheduled job's detail,
 * rendered like an ESC card: inline in the Cockpit bottom strip or as a popup on
 * Columns/Map (PegasusJobDetailModal). The dispatcher's command surface:
 * prominent customer name/address/phone, quick-edit technician / promised date
 * (MM/DD/YYYY) / arrival window / call-ahead (structured), a status change
 * (Scheduled / On hold / Cancelled / Completed — replaces the old delete), a
 * Reschedule button, the job description, and an ACTIVITY TIMELINE (slice 2) —
 * every change (tech/window/date/status/call-ahead/board-drag) and stamped notes
 * in one chronological stream (ServiceTitan/HubSpot feed). Reads the LIVE job by
 * id so edits never snap back. ESC is never touched.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Copyable, mailingLabel } from "@/components/Copyable";
import {
  Phone, UserRound, Sparkles, MapPin, CalendarClock, Pencil, Check, Plus,
  CalendarCheck, ArrowLeftRight, MessageSquare, CircleDashed, Send, Ban,
  RotateCcw, X, Trash2, AlertTriangle, FileText, ChevronDown, ChevronRight, Wrench, Clock,
  CalendarPlus, CalendarDays,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { formatDateUS, formatDateTimeUS, TIME_WINDOWS, INSTALL_TIME_WINDOWS, INSTALL_WINDOW_LABELS } from "@/lib/format";
import { useServiceTypes } from "@/features/customer-360/serviceTypes";
import { useCurrentUser } from "@/lib/currentUser";
import { useStaffNames, staffFirst } from "@/lib/staffNames";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { windowColorClasses, useActiveTechs } from "./api";
import {
  useUpdateTicket,
  useSetTicketStatus,
  usePendingTicketById,
  useDispatchEvents,
  useLogDispatchEvent,
  useRescheduleLogger,
  SOURCE_LABEL,
  type PendingTicket,
} from "./queue";
import { CreateDispatchModal } from "@/features/customer-360/CreateDispatchModal";
import { CompletedJobSummary } from "./CompletedJobSummary";
import { InvoiceBuilder } from "@/features/invoice/InvoiceBuilder";
import { useCommsAutomations, useEnqueueOutbox, fillTokens, firstNameOf } from "@/features/comms/comms";
import { useAddAnotherDay, useJobVisits, useRemoveVisit } from "@/features/jobs/jobs";
import { NotesEditor } from "@/components/NotesEditor";
import { DispatchFiles } from "@/features/files/DispatchFiles";
import { CallRecordingPlayer } from "@/features/calls/CallRecordingPlayer";
import { CallSummaryPanel } from "@/features/calls/CallSummaryPanel";

const STATUS_OPTIONS = [
  { v: "scheduled", label: "Scheduled" },
  { v: "on_hold", label: "On Hold" },
  { v: "completed", label: "Completed" },
  { v: "cancelled", label: "Cancelled" },
] as const;

function statusClasses(s: string | null): string {
  switch (s) {
    case "on_hold": return "bg-amber-50 text-amber-700 border-amber-200";
    case "completed": return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "cancelled": return "bg-rose-50 text-rose-700 border-rose-200";
    default: return "bg-blue-50 text-blue-700 border-blue-200";
  }
}

const STATUS_LABEL: Record<string, string> = Object.fromEntries(STATUS_OPTIONS.map((o) => [o.v, o.label]));

// Transitions offered by the status pill's menu. "Completed" is deliberately NOT here —
// you complete a dispatch through the "Complete & invoice" verb (which also builds the
// invoice), so the menu never duplicates that button (Seth 2026-06-26: the dropdown +
// the button read as the same thing and were confusing). The pill shows state; these
// change it.
function statusTransitions(cur: string | null): { v: "scheduled" | "on_hold" | "cancelled"; label: string }[] {
  switch (cur) {
    case "on_hold": return [{ v: "scheduled", label: "Resume (Scheduled)" }, { v: "cancelled", label: "Cancel Dispatch" }];
    case "completed":
    case "cancelled": return [{ v: "scheduled", label: "Reopen (Scheduled)" }];
    default: return [{ v: "on_hold", label: "Put on Hold" }, { v: "cancelled", label: "Cancel Dispatch" }];
  }
}

function eventIcon(kind: string) {
  switch (kind) {
    case "created": return Plus;
    case "scheduled": return CalendarCheck;
    case "rescheduled": case "date_changed": case "window_changed": return ArrowLeftRight;
    case "tech_changed": return UserRound;
    case "call_ahead_changed": return Phone;
    case "status_changed": return CircleDashed;
    case "cancelled": return Ban;
    case "deleted": return Trash2;
    case "restored": return RotateCcw;
    case "note": return MessageSquare;
    default: return CircleDashed;
  }
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
      {children}
    </div>
  );
}

/** A framed "window" — a bordered card with an optional header strip — so each part of
 *  the panel reads as its own section instead of one cramped sheet (Seth 2026-06-26:
 *  "make sections feel more like sections and windows"). Title omitted = the child
 *  brings its own header (e.g. DispatchFiles). */
function Win({
  title,
  right,
  children,
  className,
}: {
  title?: string;
  right?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("overflow-hidden rounded-2xl border border-hairline bg-white shadow-e1", className)}>
      {title && (
        <header className="flex items-center justify-between gap-2 border-b border-hairline bg-slate-50/70 px-4 py-2">
          <h4 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{title}</h4>
          {right}
        </header>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

function dispatchLabel(job: PendingTicket): string {
  return job.dispatch_no != null ? `Dispatch ${job.dispatch_no}` : `Ref ${job.id.slice(0, 8)}`;
}
function initials(name: string): string {
  const p = name.trim().split(/\s+/);
  return ((p[0]?.[0] ?? "") + (p[1]?.[0] ?? "")).toUpperCase() || "?";
}

export function PegasusJobDetailBody({
  job,
  onClose,
}: {
  job: PendingTicket;
  onClose: () => void;
  showHeader?: boolean;
  variant?: "modal" | "strip";
}) {
  const navigate = useNavigate();
  const update = useUpdateTicket();
  const setStatus = useSetTicketStatus();
  const log = useLogDispatchEvent();
  const techs = useActiveTechs("03").data;
  const live = usePendingTicketById(job.id).data ?? job;
  const events = useDispatchEvents(job.id).data ?? [];
  // DISPATCH-DELETE (Seth 2026-06-26): deleting a dispatch is a separate, granted
  // permission — admin holds it by default; others get it per-user. ESC dispatches
  // are read-only, so this surface (always Pegasus-native) is the only place delete lives.
  const canDelete = useCan("dispatch.delete");
  const me = useCurrentUser();
  const staff = useStaffNames().data;
  // MULTI-DAY: a dispatch is a visit of a job. If it belongs to one, these are the
  // sibling visits (this day + the others); addDay promotes a lone call to a job + a new day.
  const addDay = useAddAnotherDay();
  const removeVisit = useRemoveVisit();
  const { data: serviceTypes } = useServiceTypes();
  const visits = useJobVisits(live.job_id ?? null).data ?? [];
  // Optimistic: a just-removed visit drops out of the strip instantly; the soft-delete +
  // refetch (and any job-promotion revert) confirm it a beat later, so Remove feels immediate
  // even though the revert does several writes.
  const [removedVisitIds, setRemovedVisitIds] = useState<string[]>([]);
  const shownVisits = visits.filter((v) => !removedVisitIds.includes(v.id));
  const visitIndex = shownVisits.findIndex((v) => v.id === live.id);

  const c = windowColorClasses(live.scheduled_window);
  const urgent = live.urgency === "asap";

  const [draft, setDraft] = useState(live.notes ?? "");
  useEffect(() => setDraft(live.notes ?? ""), [live.id, live.notes]);
  const dirty = draft !== (live.notes ?? "");

  const [editPhone, setEditPhone] = useState(false);
  const [reschedule, setReschedule] = useState(false);
  const [invoicing, setInvoicing] = useState(false);
  const [note, setNote] = useState("");
  const [statusMenu, setStatusMenu] = useState(false);
  // The brand-new visit awaiting scheduling (opens the schedule modal on it).
  const [scheduling, setScheduling] = useState<PendingTicket | null>(null);
  // Delete is a two-step (Delete → confirm) + a recoverable deleted state, so a
  // single misclick never quietly removes a call ([[feedback_review_before_commit]]).
  const [confirmDel, setConfirmDel] = useState(false);
  const [justDeleted, setJustDeleted] = useState(false);
  // Installs use their own arrival-window set in the quick-edit below (Seth 2026-06-30).
  const [customWin, setCustomWin] = useState(false);
  const isInstall = (serviceTypes ?? []).find((t) => t.name === live.service_type_name)?.category === "install";
  const logReschedule = useRescheduleLogger();
  const rescheduleBefore = useRef<{ date: string | null; window: string | null; empno: string | null } | null>(null);
  const openReschedule = () => {
    rescheduleBefore.current = { date: live.scheduled_date, window: live.scheduled_window, empno: live.assigned_empno };
    setReschedule(true);
  };
  const closeReschedule = async () => {
    setReschedule(false);
    if (rescheduleBefore.current) await logReschedule(job.id, rescheduleBefore.current);
  };

  // COMMS: the on-the-way text — renders the appt_otw template and drops it in
  // the outbox (queued when live, HELD in testing — the honest path). Until the
  // mobile app's En-Route tap exists, this office button is the trigger.
  const { data: automations } = useCommsAutomations();
  const enqueue = useEnqueueOutbox();
  const [otwResult, setOtwResult] = useState<"queued" | "held" | null>(null);
  const otwAuto = automations?.find((a) => a.key === "appt_otw");
  const lateAuto = automations?.find((a) => a.key === "appt_late");
  const otwPhone = (live.call_ahead_phone ?? live.phone ?? "").replace(/\D/g, "").slice(-10);
  // Shared sender for the two courtesy texts (on-the-way / running-late).
  const sendCourtesy = async (auto: { key: string; template_sms: string | null } | undefined, label: string) => {
    if (!auto || otwPhone.length !== 10) return;
    const techName = live.assigned_empno ? techs?.find((t) => t.empno === live.assigned_empno)?.empname ?? null : null;
    const body = fillTokens(auto.template_sms ?? "", {
      // WHO-TO-REACH: greet the person the heads-up reaches, else the confirmation contact, else the account.
      first_name: firstNameOf(live.call_ahead_name ?? live.contact_name ?? live.customer_name),
      tech_first: techName ? firstNameOf(techName) : "Our technician",
      address: live.address ?? "your home",
    }).trim();
    if (!body) return;
    const status = await enqueue.mutateAsync({
      automation_key: auto.key,
      dedupe_key: `${auto.key}:${live.id}:${Date.now()}`,
      custno: live.custno,
      locno: live.locno,
      customer_name: live.customer_name,
      channel: "sms",
      to_address: `+1${otwPhone}`,
      body,
      trigger_kind: "dispatch",
      trigger_ref: live.id,
    });
    setOtwResult(status);
    log.mutate({ dispatch_id: live.id, kind: "note", summary: status === "queued" ? `${label} text queued to ${live.call_ahead_phone ?? live.phone}` : `${label} text HELD (comms in testing)` });
    window.setTimeout(() => setOtwResult(null), 6000);
  };
  const sendOtw = () => sendCourtesy(otwAuto, "On-the-way");
  const sendLate = () => sendCourtesy(lateAuto, "Running-late");

  // Instant-apply quick-edits get a transient one-click Undo (Seth 2026-06-25:
  // "change the tech or date and boom it just does it — give me an undo").
  // Decision: apply instantly + Undo, NOT a blocking confirm; the change AND the
  // undo both stay in the activity log so the trail is honest.
  const [undo, setUndo] = useState<{ patch: Partial<PendingTicket>; label: string } | null>(null);
  const undoTimer = useRef<number>(0);
  const clearUndo = () => {
    setUndo(null);
    window.clearTimeout(undoTimer.current);
  };

  // Apply an edit AND record it in the activity stream, with an Undo affordance.
  const edit = (patch: Partial<PendingTicket>, kind: string, summary: string) => {
    // Snapshot the prior value of exactly the keys we're changing so Undo restores them.
    const before: Partial<PendingTicket> = {};
    for (const k of Object.keys(patch)) {
      (before as Record<string, unknown>)[k] = (live as unknown as Record<string, unknown>)[k] ?? null;
    }
    update.mutate({ id: job.id, ...patch });
    log.mutate({ dispatch_id: job.id, kind, summary });
    setUndo({ patch: before, label: summary });
    window.clearTimeout(undoTimer.current);
    undoTimer.current = window.setTimeout(() => setUndo(null), 8000);
  };

  const doUndo = () => {
    if (!undo) return;
    update.mutate({ id: job.id, ...undo.patch });
    log.mutate({ dispatch_id: job.id, kind: "edit_undone", summary: `Undid — ${undo.label}` });
    clearUndo();
  };

  const changeStatus = (s: (typeof STATUS_OPTIONS)[number]["v"], label: string) => {
    setStatus.mutate({ id: job.id, status: s });
    log.mutate({ dispatch_id: job.id, kind: s === "cancelled" ? "cancelled" : "status_changed", summary: `Status → ${label}` });
    setStatusMenu(false);
    // Cancelling takes the call off the board, so close out; on-hold / reopen keep the
    // panel open so the dispatcher can keep working it.
    if (s === "cancelled") onClose();
  };

  const addNote = () => {
    const t = note.trim();
    if (!t) return;
    log.mutate({ dispatch_id: job.id, kind: "note", summary: t });
    setNote("");
  };

  // "Add another day" — promote this call to a job (if needed) + create a new visit,
  // then open the schedule modal on that new visit so the office picks the day. The
  // modal updates the new visit by id, so its job_id is preserved.
  const onAddDay = async () => {
    const res = await addDay.mutateAsync({
      id: live.id, job_id: live.job_id, visit_role: live.visit_role,
      custno: live.custno, locno: live.locno, customer_name: live.customer_name,
      address: live.address, city: live.city, phone: live.phone,
      summary: live.summary, service_type_name: live.service_type_name, lane_key: live.lane_key,
    });
    log.mutate({ dispatch_id: job.id, kind: "note", summary: "Added another day to this job" });
    setScheduling({
      ...live, id: res.visitId, job_id: res.jobId, visit_role: "return",
      status: "pending_schedule", scheduled_date: null, scheduled_window: null,
      scheduled_time: null, assigned_empno: null, notes: null, dispatch_no: null,
    });
  };

  // Schedule a sibling visit straight from the multi-day strip — opens the same modal
  // "Add another day" uses, pointed at that visit by id (so it's updated, not duplicated).
  const scheduleVisit = (vid: string, vrole: string | null) => {
    setScheduling({
      ...live, id: vid, job_id: live.job_id, visit_role: vrole ?? "return",
      status: "pending_schedule", scheduled_date: null, scheduled_window: null,
      scheduled_time: null, assigned_empno: null, notes: null, dispatch_no: null,
    });
  };

  // Soft-delete: hide it everywhere (the row + activity log survive) and log it.
  // NOT a hard wipe — distinct from the "Cancelled" status (a real job that isn't
  // happening). Delete = "this shouldn't exist" (test / mistake / duplicate).
  const isDeleted = Boolean(live.deleted_at) || justDeleted;
  const doDelete = () => {
    setConfirmDel(false);
    setJustDeleted(true);
    update.mutate({ id: job.id, deleted_at: new Date().toISOString(), deleted_by: me.data?.email ?? null });
    log.mutate({ dispatch_id: job.id, kind: "deleted", summary: "Deleted this dispatch" });
  };
  const doRestore = () => {
    setJustDeleted(false);
    update.mutate({ id: job.id, deleted_at: null, deleted_by: null });
    log.mutate({ dispatch_id: job.id, kind: "restored", summary: "Restored this dispatch" });
  };

  // Derived first entry (the dispatch's own birth) + the logged events.
  const created = {
    id: "created",
    kind: "created",
    summary: `Created${live.source ? ` · ${SOURCE_LABEL[live.source] ?? live.source}` : ""}`,
    actor: live.created_by,
    created_at: live.created_at,
  };
  const timeline = [created, ...events];
  // Who last touched it (events are oldest-first) — powers the header "Updated by".
  const lastActor = events.length ? events[events.length - 1].actor : null;

  // Deleted-state — an honest, recoverable end-state (not a yanked-away panel).
  // The card is already gone from the board; this stays open with a one-click Undo.
  if (isDeleted) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-rose-50 text-rose-600">
          <Trash2 className="h-6 w-6" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Dispatch Deleted</h3>
          <p className="mx-auto mt-1 max-w-sm text-sm text-slate-500">
            {live.customer_name} · {dispatchLabel(live)} is hidden from the board and schedules. Restore it if this was a mistake.
          </p>
        </div>
        <div className="mt-1 flex items-center gap-2">
          <Button variant="primary" size="sm" onClick={doRestore}>
            <RotateCcw className="h-3.5 w-3.5" /> Undo — restore it
          </Button>
          <Button variant="secondary" size="sm" onClick={onClose}>Close</Button>
        </div>
      </div>
    );
  }

  // DISPATCH-DONE-VIEW (Seth 2026-08-12): a completed dispatch opens as the
  // read-only visit record, not the live editor — "show me how long the tech
  // was there, when it wrapped, what was done, and let me see the invoice."
  // Reopen (inside, gated by the invoice ladder) flips status back to
  // scheduled, and this branch hands control back to the editor.
  if (live.status === "completed") {
    return <CompletedJobSummary live={live} onClose={onClose} />;
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Transient Undo for the last instant quick-edit (tech/date/window/call-ahead). */}
      {undo && (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 text-xs">
          <span className="min-w-0 truncate text-brand-800">
            <span className="font-semibold">Applied:</span> {undo.label}
          </span>
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={doUndo}
              className="inline-flex items-center gap-1 rounded-md bg-white px-2 py-1 font-semibold text-brand-700 shadow-e1 transition-colors hover:bg-brand-100"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Undo
            </button>
            <button
              type="button"
              onClick={clearUndo}
              aria-label="Dismiss"
              className="rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-700"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* Delete confirm — two-step so a misclick never removes a call. */}
      {confirmDel && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs">
          <span className="flex min-w-0 items-center gap-1.5 text-rose-800">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            Delete this dispatch? It's removed from the board + schedules — an admin can restore it.
          </span>
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={doDelete}
              className="inline-flex items-center gap-1 rounded-md bg-rose-600 px-2.5 py-1 font-semibold text-white transition-colors hover:bg-rose-700"
            >
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </button>
            <button
              type="button"
              onClick={() => setConfirmDel(false)}
              className="rounded-md bg-white px-2.5 py-1 font-medium text-slate-600 shadow-e1 transition-colors hover:bg-slate-50"
            >
              Keep
            </button>
          </div>
        </div>
      )}

      {/* Header — identity is the loudest thing on the panel. */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand-100 text-base font-semibold text-brand-700">
            {initials(live.customer_name)}
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Sparkles className="h-4 w-4 shrink-0 text-brand-500" />
              <h3 className="truncate text-xl font-semibold text-slate-900">{live.customer_name}</h3>
              {urgent && <span className="rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">ASAP</span>}
              {/* Status PILL — the current state at a glance; click for the valid state
                  changes. Never offers "Completed" — that's the Complete & invoice verb. */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setStatusMenu((o) => !o)}
                  title="Change status"
                  className={cn("inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors", statusClasses(live.status))}
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
                  {STATUS_LABEL[live.status ?? "scheduled"] ?? "Scheduled"}
                  <ChevronDown className="h-3 w-3 opacity-60" />
                </button>
                {statusMenu && (
                  <>
                    <button type="button" aria-hidden tabIndex={-1} onClick={() => setStatusMenu(false)} className="fixed inset-0 z-10 cursor-default" />
                    <div className="absolute left-0 top-full z-20 mt-1 w-48 overflow-hidden rounded-lg border border-hairline bg-white py-1 shadow-lg">
                      {statusTransitions(live.status).map((t) => (
                        <button
                          key={t.v}
                          type="button"
                          onClick={() => changeStatus(t.v, STATUS_LABEL[t.v])}
                          className={cn("flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors hover:bg-slate-50", t.v === "cancelled" ? "text-rose-700" : "text-slate-700")}
                        >
                          {t.v === "cancelled" ? <Ban className="h-3.5 w-3.5" /> : t.v === "on_hold" ? <CircleDashed className="h-3.5 w-3.5" /> : <RotateCcw className="h-3.5 w-3.5" />}
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm text-slate-600">
              {(live.address || live.city) && (
                <Copyable
                  text={mailingLabel({ name: live.customer_name, address: live.address, city: live.city })}
                  label="Copy name + address"
                >
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5 text-slate-400" />
                    {[live.address, live.city].filter(Boolean).join(", ")}
                  </span>
                </Copyable>
              )}
              {live.phone && (
                <Copyable text={live.phone} label="Copy phone">
                  <a href={`tel:${live.phone}`} className="inline-flex items-center gap-1 text-brand-700 hover:underline">
                    <Phone className="h-3.5 w-3.5" /> {live.phone}
                  </a>
                </Copyable>
              )}
              <span className="text-xs text-slate-400">{live.service_type_name ?? live.summary}</span>
            </div>
            {/* Provenance (Seth 2026-06-26): who created this + when, at a glance —
                not buried in the activity feed. The assigned tech (who's working
                it) is the Technician field below. */}
            <div className="mt-1 text-[11px] text-slate-400">
              Created by {staffFirst(staff, live.created_by)} · {formatDateTimeUS(live.created_at)}
              {live.updated_at &&
                new Date(live.updated_at).getTime() - new Date(live.created_at).getTime() > 60000 && (
                  <> · Updated {formatDateTimeUS(live.updated_at)}{lastActor ? ` by ${staffFirst(staff, lastActor)}` : ""}</>
                )}
            </div>
          </div>
        </div>
        {/* Action toolbar — VERBS only now (status moved to the pill by the name, so
            "Completed in a dropdown" no longer competes with "Complete & invoice").
            Primary action leads; Reschedule / Open customer follow; Delete is set apart. */}
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <div className="flex flex-wrap items-center justify-end gap-1.5">
            <button
              type="button"
              onClick={() => setInvoicing(true)}
              className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-brand-700 px-3 text-xs font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800"
            >
              <FileText className="h-3.5 w-3.5" /> Complete &amp; invoice
            </button>
            <button
              type="button"
              onClick={openReschedule}
              className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-hairline bg-white px-2.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50"
            >
              <CalendarClock className="h-3.5 w-3.5 text-slate-400" /> Reschedule
            </button>
            <button
              type="button"
              onClick={() => live.custno && navigate(`/customers/${live.custno}`)}
              className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-hairline bg-white px-2.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50"
            >
              <UserRound className="h-3.5 w-3.5 text-slate-400" /> Open customer
            </button>
            {/* Delete — permission-gated (dispatch.delete), set apart by a divider so it
                doesn't read as just another action. Distinct from the Cancelled status. */}
            {canDelete && (
              <>
                <span aria-hidden className="mx-0.5 h-5 w-px bg-slate-200" />
                <button
                  type="button"
                  onClick={() => setConfirmDel(true)}
                  title="Delete this dispatch (recoverable)"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-transparent text-rose-500 transition-colors hover:border-rose-200 hover:bg-rose-50"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
          <span className="text-xs text-slate-400">{dispatchLabel(live)}</span>
        </div>
      </div>

      {/* CALL STATUS — the what/when of this call, standing on its own (Seth 2026-06-26:
          make the type/date/window stand alone, then make it obvious where to change
          them). The Schedule window right below is that editor. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-brand-100 bg-brand-50/60 px-4 py-3">
        <span className="inline-flex items-center gap-1.5 text-base font-semibold text-slate-900">
          <Wrench className="h-4 w-4 text-brand-600" /> {live.service_type_name ?? live.summary ?? "Service call"}
        </span>
        <span aria-hidden className="h-4 w-px bg-brand-200" />
        <span className="inline-flex items-center gap-1.5 text-base font-semibold text-slate-900">
          <CalendarClock className="h-4 w-4 text-brand-500" />
          {live.scheduled_date
            ? `${new Date(live.scheduled_date + "T00:00:00").toLocaleDateString("en-US", { weekday: "short" })} · ${formatDateUS(live.scheduled_date)}`
            : "Unscheduled"}
        </span>
        <span aria-hidden className="h-4 w-px bg-brand-200" />
        <span className={cn("inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-sm font-semibold", live.scheduled_window ? cn(c.bg, c.text) : "border border-slate-200 bg-white text-slate-500")}>
          <Clock className="h-3.5 w-3.5" /> {live.scheduled_window ?? "Any time"}
        </span>
      </div>

      {/* PART-OF-JOB (Seth 2026-08-18: "drill back into the job if I double
          click?") — every job visit on the board carries its way home. One
          click from the dispatch card to the full install workspace. */}
      {live.job_id && (
        <button
          type="button"
          onClick={() => navigate(`/jobs/${live.job_id}`)}
          className="flex w-full items-center justify-between gap-2 rounded-xl border border-brand-200 bg-white px-4 py-2.5 text-left transition-colors hover:bg-brand-50"
        >
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-brand-800">
            <Wrench className="h-4 w-4 text-brand-600" />
            Part of an install job — equipment, permits, costing &amp; close-out live there
          </span>
          <span className="inline-flex shrink-0 items-center gap-1 text-[13px] font-bold text-brand-700">
            Open job <ChevronRight className="h-4 w-4" />
          </span>
        </button>
      )}

      {/* MULTI-DAY — when this call spans more than one day, the sibling visits live under
          one job + one invoice. Shows which day this is + the others. */}
      {live.job_id && shownVisits.length > 1 && (
        <div className="rounded-xl border border-violet-100 bg-violet-50/50 px-4 py-3">
          <div className="mb-2 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-violet-700">
            <CalendarDays className="h-3.5 w-3.5" /> Multi-day job · Visit {visitIndex >= 0 ? visitIndex + 1 : "—"} of {shownVisits.length} · one invoice
          </div>
          <div className="space-y-1">
            {shownVisits.map((v, i) => {
              const here = v.id === live.id;
              return (
                <div key={v.id} className={cn("flex items-center gap-2 rounded-md px-2 py-1 text-sm", here ? "bg-white font-medium text-slate-800 shadow-e1" : "text-slate-600")}>
                  <span className="w-14 shrink-0 text-xs font-semibold text-violet-600">Visit {i + 1}</span>
                  <span className="min-w-0 truncate">
                    {v.scheduled_date ? formatDateUS(v.scheduled_date) : "Unscheduled"}{v.scheduled_window ? ` · ${v.scheduled_window}` : ""}
                  </span>
                  {here && <span className="rounded bg-violet-100 px-1.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700">This visit</span>}
                  {!here && v.status === "pending_schedule" ? (
                    <span className="ml-auto flex shrink-0 items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => scheduleVisit(v.id, v.visit_role)}
                        className="inline-flex items-center gap-1 rounded-full bg-violet-600 px-2.5 py-0.5 text-[11px] font-semibold text-white transition-colors hover:bg-violet-700"
                      >
                        <CalendarClock className="h-3 w-3" /> Schedule
                      </button>
                      <button
                        type="button"
                        onClick={() => { setRemovedVisitIds((s) => [...s, v.id]); removeVisit.mutate({ visitId: v.id, jobId: live.job_id }); }}
                        disabled={removeVisit.isPending}
                        className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium text-slate-500 transition-colors hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50"
                      >
                        <Trash2 className="h-3 w-3" /> Remove
                      </button>
                    </span>
                  ) : (
                    <span className="ml-auto shrink-0 text-[10px] uppercase tracking-wide text-slate-400">
                      {v.status === "pending_schedule" ? "needs scheduling" : v.status}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* SCHEDULE window — the editor for the status banner above: change the tech, date,
          window, or heads-up here. */}
      <Win title="Schedule" right={<span className="text-[11px] font-normal normal-case text-slate-400">Change the details here</span>}>
        <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(160px,1fr))]">
        <Field label="Technician">
          <select
            value={live.assigned_empno ?? ""}
            onChange={(e) => {
              const empno = e.target.value || null;
              const name = empno ? techs?.find((t) => t.empno === empno)?.empname ?? empno : "Unassigned";
              edit({ assigned_empno: empno }, "tech_changed", `Tech → ${name}`);
            }}
            className="w-full rounded-md border border-hairline px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
          >
            <option value="">Unassigned</option>
            {techs?.map((t) => <option key={t.empno} value={t.empno}>{t.empname ?? t.empno}</option>)}
          </select>
        </Field>

        <Field label="Promised date">
          <input
            type="date"
            value={live.scheduled_date ?? ""}
            onChange={(e) => {
              const v = e.target.value || null;
              edit({ scheduled_date: v }, "date_changed", `Date ${formatDateUS(live.scheduled_date) || "—"} → ${formatDateUS(v) || "—"}`);
            }}
            className="w-full rounded-md border border-hairline px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
          />
          {live.scheduled_date && <div className="mt-0.5 text-[11px] text-slate-400">{formatDateUS(live.scheduled_date)}</div>}
        </Field>

        <Field label={isInstall ? "Arrival window (install)" : "Arrival window (promise)"}>
          <div className="flex flex-wrap items-center gap-1">
            {(isInstall ? INSTALL_TIME_WINDOWS : (TIME_WINDOWS as readonly string[])).map((w) => {
              const on = live.scheduled_window === w;
              return (
                <button
                  key={w}
                  type="button"
                  onClick={() => { setCustomWin(false); edit({ scheduled_window: w }, "window_changed", `Window ${live.scheduled_window ?? "Any"} → ${w}`); }}
                  className={cn("rounded-md border px-2 py-1 text-xs font-medium transition-colors", on ? cn("border-transparent", c.bg, c.text) : "border-hairline text-slate-500 hover:bg-slate-50")}
                >
                  {isInstall ? (INSTALL_WINDOW_LABELS[w] ?? w) : w}
                </button>
              );
            })}
            {/* Installs can set a one-off custom window (e.g. "7:30–9"). */}
            {isInstall && (
              <button
                type="button"
                onClick={() => setCustomWin(true)}
                className={cn(
                  "rounded-md border px-2 py-1 text-xs font-medium transition-colors",
                  customWin || (!!live.scheduled_window && !INSTALL_TIME_WINDOWS.includes(live.scheduled_window))
                    ? cn("border-transparent", c.bg, c.text)
                    : "border-hairline text-slate-500 hover:bg-slate-50",
                )}
              >
                Custom…
              </button>
            )}
            <button
              type="button"
              onClick={() => { setCustomWin(false); edit({ scheduled_window: null }, "window_changed", `Window ${live.scheduled_window ?? "Any"} → Any`); }}
              className={cn("rounded-md border px-2 py-1 text-xs font-medium transition-colors", live.scheduled_window ? "border-hairline text-slate-500 hover:bg-slate-50" : "border-transparent bg-slate-200 text-slate-700")}
            >
              Any
            </button>
            {isInstall && (customWin || (!!live.scheduled_window && !INSTALL_TIME_WINDOWS.includes(live.scheduled_window))) && (
              <input
                type="text"
                defaultValue={live.scheduled_window ?? ""}
                placeholder="e.g. 7:30–9"
                onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== live.scheduled_window) edit({ scheduled_window: v }, "window_changed", `Window ${live.scheduled_window ?? "Any"} → ${v}`); }}
                className="w-24 rounded-md border border-hairline px-2 py-1 text-xs focus:border-brand-400 focus:outline-none"
              />
            )}
          </div>
        </Field>

        <Field label="Heads-up on the way">
          <div className="space-y-1.5">
            {/* Call / Text / Off — always-visible toggles (Seth 2026-06-26: drop the
                "Add heads-up" button, just show the text/call toggles). */}
            <div className="inline-flex overflow-hidden rounded-md border border-slate-300">
              {([
                { v: "call", label: "Call" },
                { v: "text", label: "Text" },
                { v: "off", label: "Off" },
              ] as const).map((opt) => {
                const sel = opt.v === "off" ? !live.call_ahead : !!live.call_ahead && (live.call_ahead_method ?? "call") === opt.v;
                return (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() =>
                      opt.v === "off"
                        ? edit({ call_ahead: false }, "call_ahead_changed", "Heads-up off")
                        : edit(
                            { call_ahead: true, call_ahead_method: opt.v, call_ahead_phone: live.call_ahead_phone ?? live.phone ?? null },
                            "call_ahead_changed",
                            `Heads-up → ${opt.v === "text" ? "Text" : "Call"} on the way`,
                          )
                    }
                    aria-pressed={sel}
                    className={cn(
                      "px-3 py-1 text-xs font-medium transition-colors",
                      sel ? (opt.v === "off" ? "bg-slate-200 text-slate-700" : "bg-brand-600 text-white") : "bg-white text-slate-600 hover:bg-slate-50",
                    )}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            {live.call_ahead && (
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 shrink-0 text-brand-600" />
                {editPhone ? (
                  <input
                    type="tel"
                    autoFocus
                    defaultValue={live.call_ahead_phone ?? live.phone ?? ""}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v) edit({ call_ahead_phone: v }, "call_ahead_changed", `Heads-up number → ${v}`);
                      setEditPhone(false);
                    }}
                    onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); if (e.key === "Escape") setEditPhone(false); }}
                    className="w-32 rounded border border-slate-400 px-1.5 py-1 text-sm"
                  />
                ) : (
                  <button type="button" onClick={() => setEditPhone(true)} className="inline-flex items-center gap-1 text-sm text-slate-800 hover:text-brand-700" title="Change the number">
                    {live.call_ahead_phone ?? live.phone ?? "add number"} <Pencil className="h-3 w-3 text-slate-400" />
                  </button>
                )}
              </div>
            )}
          </div>
        </Field>
        </div>
        {/* Add another day — promotes a lone call to a multi-day job + opens scheduling
            for the new visit (Seth: "what if it's a multi-day job"). */}
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-hairline pt-2.5">
          <span>
            <button
              type="button"
              onClick={onAddDay}
              disabled={addDay.isPending}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 transition-colors hover:text-brand-900 disabled:opacity-50"
            >
              <CalendarPlus className="h-3.5 w-3.5" /> {addDay.isPending ? "Adding…" : "Add another day"}
            </button>
            <span className="ml-2 text-[11px] text-slate-400">multi-day job · bills as one invoice</span>
          </span>
          {/* COMMS: courtesy texts — office triggers until the tech app's taps. */}
          {otwPhone.length === 10 && (
            <span className="inline-flex items-center gap-3">
              {otwResult ? (
                <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold", otwResult === "queued" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700")}>
                  {otwResult === "queued" ? "Queued — sending shortly" : "Held — comms in testing (see Automations)"}
                </span>
              ) : (
                <>
                  {otwAuto?.enabled && (
                    <button
                      type="button"
                      onClick={sendOtw}
                      disabled={enqueue.isPending}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 transition-colors hover:text-brand-900 disabled:opacity-50"
                    >
                      <MessageSquare className="h-3.5 w-3.5" /> Text “on the way”
                    </button>
                  )}
                  {lateAuto?.enabled && (
                    <button
                      type="button"
                      onClick={sendLate}
                      disabled={enqueue.isPending}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 transition-colors hover:text-amber-900 disabled:opacity-50"
                    >
                      <Clock className="h-3.5 w-3.5" /> Text “running late”
                    </button>
                  )}
                </>
              )}
            </span>
          )}
        </div>
      </Win>

      {/* Content windows — Job description, Files, and the Activity log, each framed
          as its own section so the panel reads as distinct windows, not one sheet.
          Job + its files on the left, the activity log on the right; one column narrow. */}
      <div className="grid grid-cols-1 gap-x-5 gap-y-4 lg:grid-cols-2">
        <div className="space-y-4">
          {/* Call recording (CALL-REC-1) — when this dispatch was spawned by a
              phone call (missed-call ticket), play it inline. Renders nothing
              until a recording exists, so it's invisible on non-call jobs. */}
          {live.source_call_id && (
            <section className="space-y-2">
              <CallRecordingPlayer callId={live.source_call_id} />
              {/* AI-INTAKE-SUMMARY: listen to the call → propose notes + a
                  price-to-confirm (never auto-applies the charge). */}
              <CallSummaryPanel
                callId={live.source_call_id}
                onUseNotes={(text) => setDraft((d) => (d && d.trim() ? `${d}\n\n${text}` : text))}
                onUsePrice={(price) => edit({ diag_fee: price }, "fee_changed", `Dispatch price → $${price} (from call)`)}
              />
            </section>
          )}

          {/* Job description window — what the dispatcher needs to read fast. */}
          <Win title="Job description">
            <NotesEditor
              value={draft}
              onChange={setDraft}
              rows={3}
              placeholder="What's the job… (type or tap Dictate, then Clean up)"
            />
            {dirty && (
              <div className="mt-2">
                <Button variant="primary" size="sm" loading={update.isPending} onClick={() => update.mutate({ id: job.id, notes: draft })}>
                  <Check className="h-3.5 w-3.5" /> Save description
                </Button>
              </div>
            )}
          </Win>

          {/* Files & photos window — DispatchFiles brings its own header, so this Win
              is headerless (just the frame). Shared store; also on the 360 + mobile. */}
          {live.custno && (
            <Win>
              <DispatchFiles dispatch={live.id} custno={live.custno} locno={live.locno} />
            </Win>
          )}
        </div>

        {/* Activity window — the log-style flow of everything that happened. */}
        <Win title="Activity">
          <div className="max-h-56 overflow-auto pr-1">
            {timeline.map((e, i) => {
              const Icon = eventIcon(e.kind);
              const last = i === timeline.length - 1;
              return (
                <div key={e.id} className={cn("relative flex gap-3 pl-6", !last && "border-l-[1.5px] border-hairline pb-3 ml-[9px]")}>
                  <span className="absolute -left-[9px] top-0 flex h-[18px] w-[18px] items-center justify-center rounded-full bg-slate-100">
                    <Icon className="h-3 w-3 text-slate-500" />
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm text-slate-800">{e.summary}</div>
                    <div className="text-[11px] text-slate-400">{formatDateTimeUS(e.created_at)} · {staffFirst(staff, e.actor)}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-2 flex gap-2">
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addNote(); }}
              placeholder="Add a note — stamps the date, time and your name…"
              className="flex-1 rounded-md border border-hairline px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            />
            <Button variant="secondary" size="sm" disabled={!note.trim()} onClick={addNote}>
              <Send className="h-3.5 w-3.5" /> Add note
            </Button>
          </div>
        </Win>
      </div>

      {reschedule && (
        <CreateDispatchModal
          existingTicket={live}
          custno={live.custno}
          locno={live.locno}
          customerName={live.customer_name}
          address={live.address}
          city={live.city}
          phone={live.phone}
          onClose={closeReschedule}
        />
      )}

      {invoicing && <InvoiceBuilder dispatch={live} onClose={() => setInvoicing(false)} />}

      {/* Schedule the brand-new visit from "Add another day" — same modal Reschedule
          uses, pointed at the new (unscheduled) visit. It updates that row by id, so the
          visit's job_id is preserved. */}
      {scheduling && (
        <CreateDispatchModal
          existingTicket={scheduling}
          custno={scheduling.custno}
          locno={scheduling.locno}
          customerName={scheduling.customer_name}
          address={scheduling.address}
          city={scheduling.city}
          phone={scheduling.phone}
          onClose={() => setScheduling(null)}
        />
      )}
    </div>
  );
}

/** Popup detail for the Columns / Map board views (the Cockpit uses the strip). */
export function PegasusJobDetailModal({ job, onClose }: { job: PendingTicket | null; onClose: () => void }) {
  if (!job) return <Modal open={false} onClose={onClose} children={null} />;
  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      icon={<Sparkles className="h-4 w-4 text-brand-500" />}
      title={job.customer_name}
      subtitle={dispatchLabel(job)}
    >
      <PegasusJobDetailBody job={job} onClose={onClose} />
    </Modal>
  );
}
